import { useEffect, useState } from 'react';
import type { ArchiveRecord, GalleryFileEntry } from '../types/moderation';
import { detachFile, getSignedUrl } from '../lib/api';
import { getProcessedUrl, getThumbnailUrl } from '../lib/media';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './ui/Dialog';
import EmptyState from './ui/EmptyState';

interface MediaGalleryProps {
  record: ArchiveRecord;
  onUpdated: (record: ArchiveRecord) => void;
}

type FileKind = 'image' | 'pdf' | 'audio' | 'video' | 'other';

function detectFileKind(file: GalleryFileEntry): FileKind {
  const format = (file.processing?.format || file.contentType || '').toLowerCase();
  const key = (file.processedKey || file.preservation?.originalKey || '').toLowerCase();
  if (format.startsWith('image/') || /\.(jpe?g|png|gif|webp|tiff?|heic|avif)$/i.test(key)) {
    return 'image';
  }
  if (format.includes('pdf') || /\.pdf$/i.test(key)) {
    return 'pdf';
  }
  if (format.startsWith('audio/') || /\.(mp3|wav|ogg|m4a|aac)$/i.test(key)) {
    return 'audio';
  }
  if (format.startsWith('video/') || /\.(mp4|webm|mov|m4v)$/i.test(key)) {
    return 'video';
  }
  return 'other';
}

function detectKindLabel(kind: FileKind): string {
  switch (kind) {
    case 'image':
      return 'Image';
    case 'pdf':
      return 'Document';
    case 'audio':
      return 'Audio';
    case 'video':
      return 'Video';
    default:
      return 'File';
  }
}

function formatFileSize(bytes?: number): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

interface ExternalVideoMatch {
  provider: 'youtube' | 'vimeo';
  embedUrl: string;
}

/** Detect YouTube / Vimeo URLs and return the corresponding embed URL. */
export function parseExternalVideoUrl(raw: string): ExternalVideoMatch | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, '').toLowerCase();
  if (host === 'youtube.com' || host === 'm.youtube.com') {
    const id = url.searchParams.get('v');
    if (id) {
      return { provider: 'youtube', embedUrl: `https://www.youtube.com/embed/${id}` };
    }
    const shortsMatch = url.pathname.match(/^\/(shorts|embed)\/([\w-]+)/);
    if (shortsMatch) {
      return {
        provider: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${shortsMatch[2]}`,
      };
    }
    return null;
  }
  if (host === 'youtu.be') {
    const id = url.pathname.replace(/^\//, '').split('/')[0];
    if (id) {
      return { provider: 'youtube', embedUrl: `https://www.youtube.com/embed/${id}` };
    }
    return null;
  }
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const id = url.pathname.replace(/^\//, '').split('/')[0];
    if (id && /^\d+$/.test(id)) {
      return {
        provider: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${id}`,
      };
    }
    return null;
  }
  return null;
}

interface LightboxBodyProps {
  file: GalleryFileEntry;
  kind: FileKind;
  signedUrl: string | null;
  signedUrlError: string | null;
  signedUrlLoading: boolean;
  transcript?: string;
}

function LightboxBody({
  file,
  kind,
  signedUrl,
  signedUrlError,
  signedUrlLoading,
  transcript,
}: LightboxBodyProps) {
  if (kind === 'image') {
    return (
      <img
        src={getProcessedUrl(file)}
        alt={file.altText || ''}
        className="lightbox-image"
        data-testid="lightbox-image"
      />
    );
  }
  if (signedUrlLoading) {
    return (
      <div className="lightbox-placeholder" data-testid="lightbox-loading">
        Loading preview…
      </div>
    );
  }
  if (signedUrlError) {
    return (
      <div className="lightbox-placeholder" role="alert" data-testid="lightbox-error">
        {signedUrlError}
      </div>
    );
  }
  if (!signedUrl) {
    return (
      <div className="lightbox-placeholder" data-testid="lightbox-placeholder">
        Preview not available for this file type.
      </div>
    );
  }
  if (kind === 'pdf') {
    return (
      <iframe
        src={signedUrl}
        title={file.filename || 'PDF preview'}
        className="lightbox-pdf"
        data-testid="lightbox-pdf"
      >
        <object data={signedUrl} type="application/pdf">
          <a href={signedUrl} target="_blank" rel="noreferrer">
            Open PDF in a new tab
          </a>
        </object>
      </iframe>
    );
  }
  if (kind === 'audio') {
    return (
      <div className="lightbox-media">
        <audio
          controls
          src={signedUrl}
          className="lightbox-audio"
          data-testid="lightbox-audio"
        >
          Your browser does not support the audio element.
        </audio>
        {transcript && <TranscriptBlock transcript={transcript} />}
      </div>
    );
  }
  if (kind === 'video') {
    return (
      <div className="lightbox-media">
        <video
          controls
          src={signedUrl}
          className="lightbox-video"
          data-testid="lightbox-video"
        >
          Your browser does not support the video element.
        </video>
        {transcript && <TranscriptBlock transcript={transcript} />}
      </div>
    );
  }
  return (
    <div className="lightbox-placeholder" data-testid="lightbox-placeholder">
      <a href={signedUrl} target="_blank" rel="noreferrer">
        Download file
      </a>
    </div>
  );
}

function TranscriptBlock({ transcript }: { transcript: string }) {
  return (
    <details className="lightbox-transcript" data-testid="lightbox-transcript">
      <summary>Transcript</summary>
      <pre className="lightbox-transcript__body">{transcript}</pre>
    </details>
  );
}

interface ExternalVideoEmbedProps {
  url: string;
}

function ExternalVideoEmbed({ url }: ExternalVideoEmbedProps) {
  const match = parseExternalVideoUrl(url);
  if (!match) {
    return (
      <div className="external-video" data-testid="external-video-link">
        <span className="external-video__label">External video:</span>{' '}
        <a href={url} target="_blank" rel="noreferrer">
          {url}
        </a>
      </div>
    );
  }
  return (
    <div className="external-video" data-testid={`external-video-${match.provider}`}>
      <iframe
        src={match.embedUrl}
        title={`${match.provider} video`}
        className="external-video__iframe"
        sandbox="allow-scripts allow-same-origin allow-presentation"
        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

export default function MediaGallery({ record, onUpdated }: MediaGalleryProps) {
  const files = record.files || [];
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [detachIndex, setDetachIndex] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [signedUrlLoading, setSignedUrlLoading] = useState(false);
  const [signedUrlError, setSignedUrlError] = useState<string | null>(null);

  // Fetch a signed URL whenever the lightbox opens on a non-image file.
  useEffect(() => {
    if (lightboxIndex === null) {
      setSignedUrl(null);
      setSignedUrlError(null);
      setSignedUrlLoading(false);
      return;
    }
    const file = files[lightboxIndex];
    if (!file) return;
    if (detectFileKind(file) === 'image') {
      setSignedUrl(null);
      setSignedUrlError(null);
      setSignedUrlLoading(false);
      return;
    }
    let cancelled = false;
    setSignedUrlLoading(true);
    setSignedUrlError(null);
    setSignedUrl(null);
    getSignedUrl(record.submissionId, lightboxIndex)
      .then((res) => {
        if (cancelled) return;
        setSignedUrl(res.url);
        setSignedUrlLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSignedUrlError(
          (err as Error).message || 'Could not load preview URL.',
        );
        setSignedUrlLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lightboxIndex, files, record.submissionId]);

  const confirmDetach = async () => {
    if (detachIndex === null) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await detachFile(record.submissionId, detachIndex);
      onUpdated(updated);
      setDetachIndex(null);
    } catch (err) {
      setError((err as Error).message || 'Detach failed');
    } finally {
      setBusy(false);
    }
  };

  if (files.length === 0 && !record.externalVideoUrl) {
    return (
      <EmptyState
        title="No files attached."
        description="Contributors normally upload at least one file. This submission is text only."
      />
    );
  }

  return (
    <div className="media-gallery" data-testid="media-gallery">
      {record.externalVideoUrl && (
        <ExternalVideoEmbed url={record.externalVideoUrl} />
      )}
      <div className="media-gallery__grid">
        {files.map((file, idx) => {
          const kind = detectFileKind(file);
          const isImage = kind === 'image';
          return (
            <div key={`${file.processedKey}-${idx}`} className="media-item" data-testid={`media-item-${idx}`}>
              <button
                type="button"
                className="media-item__button"
                onClick={() => setLightboxIndex(idx)}
                aria-label={`Open ${file.filename || file.processedKey}`}
              >
                {isImage ? (
                  <img
                    src={getThumbnailUrl(file, 'md')}
                    alt={file.altText || file.filename || ''}
                    className="media-item__thumb"
                  />
                ) : (
                  <div className="media-item__placeholder">
                    <span className="media-item__kind">{detectKindLabel(kind)}</span>
                    <span className="media-item__filename">{file.filename || file.processedKey}</span>
                  </div>
                )}
              </button>
              {files.length > 1 && (
                <button
                  type="button"
                  className="btn btn-secondary media-item__detach"
                  onClick={() => setDetachIndex(idx)}
                  data-testid={`detach-${idx}`}
                >
                  Detach
                </button>
              )}
              <div
                className="media-item__metadata"
                data-testid={`media-item-metadata-${idx}`}
              >
                {file.isFeatured && (
                  <div className="media-item__meta-row media-item__featured">
                    ★ Featured
                  </div>
                )}
                {file.caption && (
                  <div className="media-item__meta-row">
                    <span className="media-item__meta-label">Caption:</span>{' '}
                    {file.caption}
                  </div>
                )}
                {file.altText && (
                  <div className="media-item__meta-row">
                    <span className="media-item__meta-label">Alt:</span>{' '}
                    {file.altText}
                  </div>
                )}
                <div className="media-item__meta-row media-item__meta-row--inline">
                  {file.contentType && <span>{file.contentType}</span>}
                  {formatFileSize(file.size) && (
                    <span>{formatFileSize(file.size)}</span>
                  )}
                  {file.catalogue?.width && file.catalogue?.height && (
                    <span>
                      {file.catalogue.width} × {file.catalogue.height}
                    </span>
                  )}
                  {file.catalogue?.duration && <span>{file.catalogue.duration}</span>}
                  {file.catalogue?.quality && (
                    <span>quality: {file.catalogue.quality}</span>
                  )}
                </div>
                {file.validation?.warnings && file.validation.warnings.length > 0 && (
                  <ul
                    className="media-item__warnings"
                    data-testid={`media-item-warnings-${idx}`}
                  >
                    {file.validation.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={lightboxIndex !== null} onOpenChange={(o) => !o && setLightboxIndex(null)}>
        {lightboxIndex !== null && files[lightboxIndex] && (
          <DialogContent className="dlg-content--lightbox">
            <DialogTitle>{files[lightboxIndex].filename || 'Preview'}</DialogTitle>
            <DialogDescription>Full resolution preview</DialogDescription>
            <LightboxBody
              file={files[lightboxIndex]}
              kind={detectFileKind(files[lightboxIndex])}
              signedUrl={signedUrl}
              signedUrlError={signedUrlError}
              signedUrlLoading={signedUrlLoading}
              transcript={record.transcript}
            />
            <div className="dlg-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setLightboxIndex(null)}>
                Close
              </button>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <Dialog open={detachIndex !== null} onOpenChange={(o) => !o && setDetachIndex(null)}>
        {detachIndex !== null && (
          <DialogContent>
            <DialogTitle>Detach file</DialogTitle>
            <DialogDescription>
              Detach this file from the submission? This cannot be undone.
            </DialogDescription>
            {error && <div className="field-error" role="alert">{error}</div>}
            <div className="dlg-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDetachIndex(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmDetach}
                disabled={busy}
                data-testid="detach-confirm"
              >
                {busy ? 'Detaching…' : 'Detach'}
              </button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
